import { intersection } from "lodash";
import { ISpiffSetupRule } from "../types/spiffSetup";
import moment from "moment";

const findIntersectingSpiffRules = (rules: ISpiffSetupRule[]) => {
	const errorDetails: { message: string; path: (number | string)[] }[] = [];

	rules.forEach((rule) => {
		const otherRules = rules.filter((otherRule) => otherRule !== rule);
		if (otherRules.length === 0) {
			return;
		}
		for (const other of otherRules) {
			const errorsFields = areSpiffRulesIntersecting(rule, other);
			if (errorsFields.length > 0) {
				const rulePosition = rules.indexOf(other);
				for (const field of errorsFields) {
					errorDetails.push({
						message: `${field} is duplicated`,
						path: [rulePosition, field],
					});
				}
			}
		}
	});
	return errorDetails;
};

// Check if two rules intersect based on all conditions
const areSpiffRulesIntersecting = (rule1: ISpiffSetupRule, rule2: ISpiffSetupRule): string[] => {
	const intersectingProps: string[] = [];

	// Check stockExpression intersection
	const stockExpressionIntersect = regexIntersects(rule1.stockExpression, rule2.stockExpression);
	if (!stockExpressionIntersect) {
		return intersectingProps;
	}

	// Check year range intersection
	const isYearsOverlap = yearsOverlap(rule1.startYear, rule1.endYear, rule2.startYear, rule2.endYear);
	if (!isYearsOverlap) {
		return intersectingProps;
	}

	// Check status intersection
	const statusIntersecting = statusIntersect(rule1.status, rule2.status);
	if (!statusIntersecting) {
		return intersectingProps;
	}

	// Check make and model intersection
	const makeAndModelIntersect = makeModelIntersect({ make1: rule1.make, model1: rule1.model, make2: rule2.make, model2: rule2.model });

	if (!makeAndModelIntersect) {
		return intersectingProps;
	}

	intersectingProps.push("stockExpression");
	intersectingProps.push("startYear", "endYear");
	intersectingProps.push("status");
	intersectingProps.push("make", "model");

	return intersectingProps;
};

// Helper function to determine if make and model are the same
const makeModelIntersect = ({
	make1,
	model1,
	make2,
	model2,
}: {
	make1: string;
	model1: string;
	make2: string;
	model2: string;
}): boolean => {
	if (!make1 || !make2) {
		return true;
	}

	if (make1 !== make2) {
		return false;
	}

	if (!model1 || !model2) {
		return true;
	}

	const model1ToCompare = model1.replace("*", "");
	const model2ToCompare = model2.replace("*", "");

	if (model1ToCompare.includes(model2ToCompare) || model2ToCompare.includes(model1ToCompare)) {
		return true;
	}

	return false;
};

// Helper function to determine if two status arrays intersect
const statusIntersect = (status1: string[], status2: string[]): boolean => {
	if (!status1.length || !status2.length) {
		return true;
	}
	return !!intersection(status1, status2).length;
};

const isYear = (year: string) => /^\d{4}$/.test(year);

// Helper function to check overlap in year ranges
const yearsOverlap = (start1: string, end1: string, start2: string, end2: string): boolean => {
	if (!start1 || !end1 || !start2 || !end2) {
		return true;
	}

	let startYear1Updated = parseInt(start1, 10);
	let startYear2Updated = parseInt(start2, 10);
	let endYear1Updated = parseInt(end1, 10);
	let endYear2Updated = parseInt(end2, 10);

	if (!isYear(start1)) {
		startYear1Updated = moment().year() + parseInt(start1, 10);
	}
	if (!isYear(start2)) {
		startYear2Updated = moment().year() + parseInt(start2, 10);
	}
	if (!isYear(end1)) {
		endYear1Updated = moment().year() + parseInt(end1, 10);
	}
	if (!isYear(end2)) {
		endYear2Updated = moment().year() + parseInt(end2, 10);
	}

	const [startYear1, endYear1] = [startYear1Updated, endYear1Updated];
	const [startYear2, endYear2] = [startYear2Updated, endYear2Updated];
	return startYear1 <= endYear2 && startYear2 <= endYear1;
};

// Determine if two regex expressions intersect
const regexIntersects = (regex1: string, regex2: string): boolean => {
	if (!regex1 || !regex2) {
		return true;
	}
	const normalizeRegex = (regex: string) => {
		const regexArray = regex.split("*");

		const startsWith = regex.startsWith("*") ? "" : regexArray[0];

		if (regexArray.length === 1) {
			return { startsWith, endsWith: "" };
		}

		const endsWith = regex.endsWith("*") ? "" : regex.split("*").pop();

		return { startsWith, endsWith };
	};

	const { startsWith: start1, endsWith: end1 } = normalizeRegex(regex1);
	const { startsWith: start2, endsWith: end2 } = normalizeRegex(regex2);

	const startsOverlap = start1 && start2 ? start2.startsWith(start1) || start1.startsWith(start2) : true;
	const endsOverlap = end1 && end2 ? end2.startsWith(end1) || end1.startsWith(end2) : true;

	return startsOverlap && endsOverlap;
};

export { findIntersectingSpiffRules, isYear };
